import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { VolumeMiniTypeEnum } from 'elements-sdk/src'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { PanelService } from 'panel/services/panel.service'
import { NotifyService } from 'common/util/services/notify.service'

import { MediaOperationsService } from '../services/mediaOperations.service'
import { BaseOperation, MediaOperationContext, MediaOperationInput } from './base'

@Injectable({ providedIn: 'root' })
class DownloadProxyOp extends BaseOperation {
  icon = 'far fa-download'
  showInSelectionToolbar = false
  showInSelectionOverflowMenu = true
  showInContextMenu = true
  showInSharedLinkView = true
  supportsMultiple = true
  supportsBundles = true
  supportsDirs = true
  weight = 81

  constructor (
    private translate: TranslateService,
    private mediaPermissions: MediaPermissionsService,
    private api: APIService,
    private panel: PanelService,
    private notify: NotifyService,
  ) {
    super()
    this.label = translate.instant('Download proxy')
  }

  isVisibleForBundle (bundle) {
    return !this.panel.active && bundle.mainfile && this.mediaPermissions.getPermissionForBundle(bundle)?.allowProxyDownload
  }

  isEnabledForBundle (bundle) {
    return bundle.asset
  }

  isVisibleForDir (dir) {
    return this.mediaPermissions.getPermissionForFile(dir)?.allowProxyDownload ?? false
  }

  isEnabledForDir (dir) {
    return dir.present
  }

  isEnabledFor (input: MediaOperationInput, context: MediaOperationContext): boolean {
    if (context.location?.volume.type === VolumeMiniTypeEnum.Cloud &&
      (input.dirs.length || input.bundles.length > 1)) {
      return false
    }
    return super.isEnabledFor(input, context)
  }

  async run ({ dirs, bundles }, context) {
    let paths = bundles.map(item => item.mainfile.fullPath)
    paths = [...paths, ...dirs.map(item => item.fullPath)]

    if (paths.length > 1 || dirs.length) {
      const tab = window.open('/download-archive/create')

      if (!tab) {
        this.notify.error(this.translate.instant('You need to allow pop-ups in your browser'))
        return
      }

      // We are still acting sync on a trusted event
      // open tab now to avoid popup blocker

      try {
        const result = await this.api.main.createArchive({
          createDownloadArchive: {
            paths,
            proxy: true,
            forRoot: context.root?.id,
          },
        })
        tab.location = `/download-archive/${result.id}` as any
      } catch (response) {
        tab.close()
        this.notify.errorFromResponse(this.translate.instant('Could not create download'), response)
      }
    } else {
      const bundle = bundles[0]
      const asset = await this.api.mediaLibrary.getAsset({ id: bundle.asset.id, forRoot: context.root?.id })
      if (!asset.defaultProxy?.urls) {
        return
      }
      window.location = `${asset.defaultProxy.urls.proxy}?bundle_id=${bundle.id}` as any
    }
  }
}

MediaOperationsService.register(DownloadProxyOp)
